import type { RefObject } from 'react';
import { useCallback, useRef, useState } from 'react';

import { useI18n } from '@change-corgi/core-react/i18n';

import type { PetitionEditPageState } from '#src/app/pages/petitionEditV2/pageContext';
import { usePetitionEditPageState } from '#src/app/pages/petitionEditV2/pageContext';

type DecisionMakerType = PetitionEditPageState['formState']['decisionMakers']['value'][number];

type UseEditDmFormReturn = ModelHookResult<
	{
		nameError: string | null;
		nameInputRef: RefObject<HTMLInputElement | null>;
		summaryInputRef: RefObject<HTMLInputElement | null>;
	},
	{
		handleOnCancel: () => void;
		handleOnSave: () => void;
		handleOnNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
	}
>;
type ViewMode = 'EDITING' | 'READONLY' | 'CREATING';
type Props = {
	decisionMaker: DecisionMakerType;
	setReadonly: () => void;
	mode: ViewMode;
	onRemoveDecisionMaker: () => void;
};

// eslint-disable-next-line max-lines-per-function
export function useEditDecisionMakerForm({
	setReadonly,
	decisionMaker,
	mode,
	onRemoveDecisionMaker,
}: Props): UseEditDmFormReturn {
	const { translate } = useI18n();
	const [state, mutations] = usePetitionEditPageState();
	const [nameError, setNameError] = useState<string | null>(null);
	const nameInputRef = useRef<HTMLInputElement>(null);
	const summaryInputRef = useRef<HTMLInputElement>(null);

	const handleOnCancel = useCallback(() => {
		if (mode === 'CREATING') onRemoveDecisionMaker();
		setReadonly();
	}, [mode, onRemoveDecisionMaker, setReadonly]);

	const handleOnSave = useCallback(() => {
		const nameValue = nameInputRef.current?.value;
		const summaryValue = summaryInputRef.current?.value || null;

		if (!nameValue) {
			setNameError(translate('corgi.petition.edit.petition_data.decision_maker.name_error'));
			return;
		}

		const updatedDecisionMakers = state.formState.decisionMakers.value.map((dm) => {
			if (dm.id === decisionMaker.id) {
				return {
					...dm,
					displayName: nameValue,
					title: summaryValue,
				};
			}
			return dm;
		});
		mutations.setDecisionMakers(updatedDecisionMakers);
		setReadonly();
	}, [decisionMaker, translate, mutations, state.formState.decisionMakers.value, setReadonly]);

	const handleOnNameChange = (_: React.ChangeEvent<HTMLInputElement>) => {
		if (nameError) setNameError(null);
	};

	return {
		data: {
			nameError,
			nameInputRef,
			summaryInputRef,
		},
		actions: {
			handleOnCancel,
			handleOnSave,
			handleOnNameChange,
		},
	};
}
